import { get, post, put, patch, del } from '../../apiClient';
import { API_ENDPOINTS } from '../../config';

const customerService = {
  /**
   * Get customer by ID
   */
  getCustomerById: async (customerId) => {
    try {
      const response = await get(`${API_ENDPOINTS.CRM.CUSTOMERS}/${customerId}`);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          customer: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve customer',
          customer: null
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to retrieve customer',
        customer: null
      };
    }
  },

  /**
   * Get all customers with filters
   */
  getCustomers: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.search) queryParams.append('search', filters.search);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.CUSTOMERS}?${queryParams.toString()}`
        : API_ENDPOINTS.CRM.CUSTOMERS;

      const response = await get(url);

      if (response && response.responseCode === 200) {
        const customers = Array.isArray(response.data)
          ? response.data
          : (response.data?.customers || []);

        return {
          success: true,
          customers: customers,
          pagination: response.data?.pagination || {}
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve customers',
          customers: []
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to retrieve customers',
        customers: []
      };
    }
  },

  /**
   * Get Customer 360 list with pagination (no KPIs)
   */
  getCustomer360List: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.search) queryParams.append('search', filters.search);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.minOrders !== undefined) queryParams.append('minOrders', filters.minOrders);
      if (filters.maxOrders !== undefined) queryParams.append('maxOrders', filters.maxOrders);
      if (filters.minValue !== undefined) queryParams.append('minValue', filters.minValue);
      if (filters.maxValue !== undefined) queryParams.append('maxValue', filters.maxValue);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.CUSTOMERS_360}?${queryParams.toString()}`
        : API_ENDPOINTS.CRM.CUSTOMERS_360;

      const response = await get(url);

      if (response && response.responseCode === 200) {
        const data = response.data || {};
        return {
          success: true,
          customers: data.data || [],
          pagination: data.pagination || { page: 1, limit: 10, total: 0 }
        };
      } else {
        return {
          success: false,
          error: response?.responseMessage || 'Failed to retrieve Customer 360 data',
          customers: [],
          pagination: { page: 1, limit: 10, total: 0 }
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to retrieve Customer 360 data',
        customers: [],
        pagination: { page: 1, limit: 10, total: 0 }
      };
    }
  },

  /**
   * Get global Customer 360 KPIs
   */
  getCustomer360Kpis: async () => {
    try {
      const url = `${API_ENDPOINTS.CRM.CUSTOMERS_360}/kpis`;
      const response = await get(url);

      if (response && response.responseCode === 200) {
        const data = response.data || {};
        return {
          success: true,
          kpis: {
            totalCustomers: data.totalCustomers || 0,
            totalOrders: data.totalOrders || 0,
            totalValue: data.totalValue || 0
          }
        };
      } else {
        return {
          success: false,
          error: response?.responseMessage || 'Failed to retrieve Customer 360 KPIs',
          kpis: {
            totalCustomers: 0,
            totalOrders: 0,
            totalValue: 0
          }
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to retrieve Customer 360 KPIs',
        kpis: {
          totalCustomers: 0,
          totalOrders: 0,
          totalValue: 0
        }
      };
    }
  },

  /**
   * Get full Customer 360 detail for a single customer
   */
  getCustomer360: async (customerId) => {
    try {
      if (!customerId) {
        return {
          success: false,
          error: 'Customer ID is required',
          data: null
        };
      }

      const url = `${API_ENDPOINTS.CRM.CUSTOMERS_360}/${customerId}`;
      const response = await get(url);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          data: response.data || null
        };
      } else {
        return {
          success: false,
          error: response?.responseMessage || 'Failed to retrieve Customer 360 detail',
          data: null
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to retrieve Customer 360 detail',
        data: null
      };
    }
  },

  /**
   * Get customer-specific KPIs
   */
  getCustomerKpis: async (customerId) => {
    try {
      if (!customerId) {
        return {
          success: false,
          error: 'Customer ID is required',
          kpis: null
        };
      }

      const url = `${API_ENDPOINTS.CRM.CUSTOMERS_360}/${customerId}/kpis`;
      const response = await get(url);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          kpis: response.data || null
        };
      } else {
        return {
          success: false,
          error: response?.responseMessage || 'Failed to retrieve Customer KPIs',
          kpis: null
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to retrieve Customer KPIs',
        kpis: null
      };
    }
  },

  /**
   * Get customer interactions (activities from linked lead) with filters and pagination
   */
  getCustomerInteractions: async (customerId, filters = {}) => {
    try {
      if (!customerId) {
        return {
          success: false,
          error: 'Customer ID is required',
          activities: [],
          pagination: {}
        };
      }

      const queryParams = new URLSearchParams();

      if (filters.type && filters.type !== 'all') queryParams.append('type', filters.type);
      if (filters.outcome) queryParams.append('outcome', filters.outcome);
      if (filters.priority) queryParams.append('priority', filters.priority);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
      if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.CUSTOMERS}/${customerId}/interactions?${queryParams.toString()}`
        : `${API_ENDPOINTS.CRM.CUSTOMERS}/${customerId}/interactions`;

      const response = await get(url);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          activities: response.data?.activities || [],
          pagination: response.data?.pagination || {}
        };
      } else {
        return {
          success: false,
          error: response?.responseMessage || 'Failed to retrieve interactions',
          activities: [],
          pagination: {}
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to retrieve interactions',
        activities: [],
        pagination: {}
      };
    }
  },

  /**
   * Update customer isActive status
   */
  updateCustomerStatus: async (customerId, isActive) => {
    try {
      if (!customerId) {
        return {
          success: false,
          error: 'Customer ID is required'
        };
      }

      const response = await patch(`${API_ENDPOINTS.CRM.CUSTOMERS}/${customerId}/status`, {
        isActive
      });

      if (response && response.responseCode === 200) {
        return {
          success: true,
          customer: response.data
        };
      } else {
        return {
          success: false,
          error: response?.responseMessage || 'Failed to update customer status'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to update customer status'
      };
    }
  },

  /**
   * Create a new customer
   */
  createCustomer: async (customerData) => {
    try {
      const response = await post(API_ENDPOINTS.CRM.CUSTOMERS, customerData);

      if (response && response.responseCode === 201) {
        return {
          success: true,
          customer: response.data
        };
      } else {
        return {
          success: false,
          error: response?.responseMessage || 'Failed to create customer'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to create customer'
      };
    }
  },

  /**
   * Update an existing customer
   */
  updateCustomer: async (customerId, customerData) => {
    try {
      if (!customerId) {
        return {
          success: false,
          error: 'Customer ID is required'
        };
      }

      const response = await put(`${API_ENDPOINTS.CRM.CUSTOMERS}/${customerId}`, customerData);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          customer: response.data
        };
      } else {
        return {
          success: false,
          error: response?.responseMessage || 'Failed to update customer'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to update customer'
      };
    }
  },

  /**
   * Export Customer 360 list to CSV
   */
  exportCustomer360List: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.search) queryParams.append('search', filters.search);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.minOrders !== undefined) queryParams.append('minOrders', filters.minOrders);
      if (filters.maxOrders !== undefined) queryParams.append('maxOrders', filters.maxOrders);
      if (filters.minValue !== undefined) queryParams.append('minValue', filters.minValue);
      if (filters.maxValue !== undefined) queryParams.append('maxValue', filters.maxValue);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);

      // Construct export URL
      const url = `${API_ENDPOINTS.CRM.CUSTOMERS_360}/export?${queryParams.toString()}`;

      // For file download, we don't use the standard fetch wrapper because we need the blob
      // We'll construct the full URL and return it for the component to handle, or handle it here
      return {
        success: true,
        downloadUrl: url
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to generate export URL'
      };
    }
  },

  /**
   * Get industries configuration
   */
  getIndustries: async () => {
    try {
      const response = await get(API_ENDPOINTS.CRM.CONFIG.INDUSTRIES);

      // Handle the case where the API returns the data directly or wrapped in a success object
      if (response && (response.responseCode === 200 || response.success === true)) {
        return {
          success: true,
          data: response.data || []
        };
      } else if (Array.isArray(response)) {
        // Fallback if the API returns just the array
        return {
          success: true,
          data: response
        };
      } else {
        return {
          success: false,
          error: response?.responseMessage || response?.error || 'Failed to retrieve industries'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to retrieve industries'
      };
    }
  },

  /**
   * Get decision makers configuration
   */
  getDecisionMakers: async () => {
    try {
      const response = await get(API_ENDPOINTS.CRM.CONFIG.DECISION_MAKERS);

      // Handle the case where the API returns the data directly or wrapped in a success object
      if (response && (response.responseCode === 200 || response.success === true)) {
        return {
          success: true,
          data: response.data || []
        };
      } else if (Array.isArray(response)) {
        // Fallback if the API returns just the array
        return {
          success: true,
          data: response
        };
      } else {
        return {
          success: false,
          error: response?.responseMessage || response?.error || 'Failed to retrieve decision makers'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to retrieve decision makers'
      };
    }
  },
  /**
   * Get company sizes configuration
   */
  getCompanySizes: async () => {
    try {
      const response = await get(API_ENDPOINTS.CRM.CONFIG.COMPANY_SIZES);

      if (response && (response.responseCode === 200 || response.success === true)) {
        return {
          success: true,
          data: response.data || []
        };
      } else if (Array.isArray(response)) {
        return {
          success: true,
          data: response
        };
      } else {
        return {
          success: false,
          error: response?.responseMessage || response?.error || 'Failed to retrieve company sizes'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to retrieve company sizes'
      };
    }
  },
  /**
   * Preview customers for bulk upload
   */
  bulkPreview: async (customers) => {
    try {
      const response = await post(`${API_ENDPOINTS.CRM.CUSTOMERS}/bulk-preview`, { customers });
      if (response && response.responseCode === 200) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response?.responseMessage || 'Failed to generate preview'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to generate preview'
      };
    }
  },

  /**
   * Perform bulk upload of customers
   */
  bulkUpload: async (customers) => {
    try {
      const response = await post(`${API_ENDPOINTS.CRM.CUSTOMERS}/bulk-upload`, { customers });
      if (response && (response.responseCode === 201 || response.responseCode === 200)) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response?.responseMessage || 'Failed to perform bulk upload'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to perform bulk upload'
      };
    }
  }
};

export default customerService;

